import { useFilesContext } from '@/contexts/filesContext.tsx'
import { Checkbox, Range } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useEffect, useMemo, useState } from 'react'
import { useClient } from '@/stores/useClient.ts'
import { useQuery } from '@tanstack/react-query'
import { AlbumFileType } from '@/models/albumFileType.ts'
import { AlbumFile, prepareForLightbox } from '@/models/albumFile.ts'
import { getDemoFilesForDrive } from '@/controller/demo.ts'
import Lightbox from 'yet-another-react-lightbox'
import { Fullscreen, Slideshow } from 'yet-another-react-lightbox/plugins'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import DriveHeader from '@/components/DriveHeader.tsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Album } from '@/models/album.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { useProfile } from '@/stores/useAuth.ts'
import FullscreenOverlay from '@/components/yarl/FullscreenOverlay.tsx'
import { MyCaptionsLiveshow } from '@/components/yarl/MyCaptionsLiveshow.tsx'
import { useTranslation } from 'react-i18next'

const defaultDelay = 3
const defaultRandom = false
const defaultAutoPlay = false
const defaultQr = false
const defaultHideDate = false

interface LiveshowURLParams {
  delay?: number
  random?: boolean
  auto?: boolean
  qr?: boolean
  hideDate?: boolean
}

export default function LiveshowPage() {
  const { t } = useTranslation()
  const { currentDrive } = useDrives()
  const { currentAlbum: album } = useFilesContext()!
  const { getLiveshow } = useClient()
  const [renderAlbumFiles, setRenderAlbumFiles] = useState<Array<AlbumFile>>([])
  const [index, setIndex] = useState<number | null>(null)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [triggerAutoplay, setTriggerAutoplay] = useState(false)
  const [isAutoplayOnLoad] = useState(searchParams.has('auto') && searchParams.get('auto'))
  const { isGuestMode } = useProfile()

  const slideDelay = (searchParams.has('delay') && parseInt(searchParams.get('delay')!)) || defaultDelay
  const isRandomOrder = (searchParams.has('random') && searchParams.get('random') == 'yes') || defaultRandom
  const isAutoPlay = (searchParams.has('auto') && searchParams.get('auto') == 'yes') || defaultAutoPlay
  const showQRCode = (searchParams.has('qr') && searchParams.get('qr') == 'yes') || defaultQr
  const hideDate = (searchParams.has('hideDate') && searchParams.get('hideDate') == 'yes') || defaultHideDate

  const { data, refetch } = useQuery({
    enabled: !!album,
    queryFn: async () => {
      const response = await getLiveshow(album!, { file_type: AlbumFileType.IMAGE })
      const files: Array<AlbumFile> = response.data.files

      return files
    },
    queryKey: ['drive', currentDrive?.id, 'liveshow'],
  })

  useEffect(() => {
    if (isAutoplayOnLoad && data != null) {
      setTriggerAutoplay(true)
    }
  }, [isAutoplayOnLoad, data, setTriggerAutoplay]);

  useEffect(() => {
    if (triggerAutoplay) {
        setIndex(0)
    }
  }, [triggerAutoplay]);

  useEffect(() => {
    const refetchInterval = 30_000
    // const refetchInterval = 500
    const intervalId = setInterval(() => {
      refetch()
    }, refetchInterval)

    return () => clearInterval(intervalId)
  }, []);

  useEffect(() => {
    refetch()
  }, [album?.id]);

  function shuffle(array: Array<AlbumFile>) {
    let currentIndex = array.length;

    // While there remain elements to shuffle...
    while (currentIndex != 0) {

      // Pick a remaining element...
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }
  }

  useEffect(() => {
    if (!data || !currentDrive || !album) {
      return
    }

    const finalFiles = [
      ...data,
      ...(currentDrive.is_demo ? getDemoFilesForDrive(currentDrive, isGuestMode, album) : [])
    ]

    // Shuffle
    if (isRandomOrder) {
      shuffle(finalFiles)
    }

    const currentFile = index ? renderAlbumFiles[index] : null
    if (currentFile) {
      const defaultIndexIfNotPresent = finalFiles.length > 0 ? 0 : null
      setIndex(getIndexOfFile(finalFiles, currentFile) || defaultIndexIfNotPresent)
    }

    setRenderAlbumFiles(finalFiles)
  }, [currentDrive, album?.id, data, isRandomOrder])

  const lightboxFiles = useMemo(() => {
    return renderAlbumFiles.map(albumFile => prepareForLightbox(albumFile, currentDrive!))
  }, [renderAlbumFiles, currentDrive])

  function onStartClick() {
    setIndex(0)
  }

  function getIndexOfFile(files: Array<AlbumFile>, albumFile: AlbumFile) {
    return files.findIndex(file => file.id === albumFile.id)
  }

  function navigateToAlbum(album: Album) {
    navigate(`/d/${currentDrive!.id}/${album.id}/liveshow?${searchParams.toString()}`)
  }

  function onRandomOrderClick() {
    updateParams({
      random: !isRandomOrder,
    })
  }

  function onIsAutoplayClick() {
    updateParams({
      auto: !isAutoPlay,
    })
  }

  function onShowQrCodeClick() {
    updateParams({
      qr: !showQRCode,
    })
  }

  function onHideDateClick() {
    updateParams({
      hideDate: !hideDate,
    })
  }

  function getUpdateParams(urlParams: LiveshowURLParams) {
    const formattedParams: Record<string, string> = {}
    const newDelay = urlParams.delay !== undefined ? urlParams.delay : slideDelay
    if (newDelay != defaultDelay) {
      formattedParams.delay = '' + newDelay
    }
    const newRandom = urlParams.random !== undefined ? urlParams.random : isRandomOrder
    if (newRandom != defaultRandom && newRandom) {
      formattedParams.random = 'yes'
    }
    const newAutoplay = urlParams.auto !== undefined ? urlParams.auto : isAutoPlay
    if (newAutoplay != defaultAutoPlay && newAutoplay) {
      formattedParams.auto = 'yes'
    }
    const newQr = urlParams.qr !== undefined ? urlParams.qr : showQRCode
    if (newQr != defaultQr && newQr) {
      formattedParams.qr = 'yes'
    }
    const newHideDate = urlParams.hideDate !== undefined ? urlParams.hideDate : hideDate
    if (newHideDate != defaultHideDate && newHideDate) {
      formattedParams.hideDate = 'yes'
    }

    return formattedParams
  }

  function updateParams(urlParams: LiveshowURLParams) {
    const params = getUpdateParams(urlParams)
    setSearchParams(params)
  }

  function onSetSlideDelay(delay: number) {
    updateParams({ delay })
  }

  if (!album || !currentDrive) {
    return <></>
  }


  return <div>
    <DriveHeader />
    <div className="container mx-auto px-4 flex flex-col gap-3 justify-center overflow-hidden items-center pb-5">
      <h2 className='font-serif text-center text-3xl'>{t('common.liveshow')}</h2>
      <Button color='primary' onClick={onStartClick}>
        {t('common.startLiveshow')}
      </Button>
      <h3 className='mt-5 text-xl font-bold'>{t('common.chooseAlbum')}</h3>
      <AlbumSelection drive={currentDrive} selectedAlbum={album} setSelectedAlbum={navigateToAlbum} />
      <p>{t('common.currentlyPictures', {count: renderAlbumFiles.length})}</p>
      <p></p>
      <h3 className='mt-5 text-xl font-bold'>{t('common.settings')}</h3>
      <label className="form-control flex flex-row items-center gap-3">
        <Checkbox color='primary' checked={isRandomOrder} onChange={() => onRandomOrderClick()}/>
        <span className="label-text select-none">{t('common.randomOrder')}</span>
      </label>
      <label className="form-control flex flex-col items-center gap-1">
        <div className='form-control flex flex-row items-center gap-3'>
          <Checkbox color='primary' checked={isAutoPlay} onChange={() => onIsAutoplayClick()} />
          <span className="label-text select-none">{t('common.autoplay')}</span>
        </div>
        { isAutoPlay && <p className="label-text select-none text-sm opacity-50">{t('common.autoplayDesc')}</p> }
      </label>
      <label className="form-control flex flex-col items-center gap-1">
        <div className='form-control flex flex-row items-center gap-3'>
          <Checkbox color='primary' checked={showQRCode} onChange={onShowQrCodeClick} />
          <span className="label-text select-none">{t('common.qrCode')}?</span>
        </div>
      </label>
      <label className="form-control flex flex-row items-center gap-3">
        <Checkbox color='primary' checked={hideDate} onChange={onHideDateClick} />
        <span className="label-text select-none">{t('common.hideDateInfo')}</span>
      </label>
      <p className='mt-3'>{t('common.secondsBetweenImages', {count: slideDelay})}</p>
      <Range className='bg-secondary' color='primary' value={slideDelay} min={1} max={30}
             onChange={e => onSetSlideDelay(parseInt(e.target.value))}/>
      { index != null && <>
        <meta name="theme-color" content="#000000" />
        <style>
          {
            'html, body { background-color: black !important; }'
          }
        </style>
      </>}
      <Lightbox
        className='lightbox-image-full'
        open={index != null}
        index={index || undefined}
        close={() => setIndex(null)}
        fullscreen={{auto: true}}
        slideshow={{autoplay: true, delay: slideDelay * 1000}}
        animation={{
          swipe: 0,
        }}
        on={{
          view: ({index}) => setIndex(index),
          click: () => setIndex((index! + 1) % lightboxFiles.length)
        }}
        plugins={[
          Slideshow,
          Fullscreen,
          ...(hideDate ? [] : [MyCaptionsLiveshow]),
        ]}
        render={{
          buttonNext: () => null,
          buttonPrev: () => null,
        }}
        slides={lightboxFiles}
      />
      { index != null && showQRCode && album && album.qr_code_image && <>
        <FullscreenOverlay>
          <div className='absolute bottom-0 right-0 p-4 pb-[6rem] z-[9999999] flex justify-end w-full'>
            <img className='max-w-[30%] aspect-square w-[14rem] rounded-lg' src={album.qr_code_image} />
          </div>
        </FullscreenOverlay>
        <div className='fixed bottom-0 right-0 p-4 pb-[6rem] z-[9999999] flex justify-end w-full'>
          <img className='max-w-[30%] aspect-square w-[14rem] rounded-lg' src={album.qr_code_image} />
        </div>
      </>}
    </div>
  </div>
}
